import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Stripe Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[5].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[5].alt_text',
      defaultValue: 'Community lifestyle',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Select a wide, atmospheric lifestyle or community photo that works as a full-width banner.',
    context:
      'Full-bleed image stripe spanning the entire page width below the amenities intro. Should be a scenic, horizontally-oriented photo, distinct from the property featured image.',
    sourceCollection: 'photo_gallery',
  },
};

// ============================================================================
// Component
// ============================================================================

export function FullBleedStripe() {
  const image = useSlotGroup('fullbleedstripe.image', imageDef);

  return (
    <section className="w-full overflow-hidden bg-[var(--vibe-color-background)]">
      <EditableSlotGroup
        groupPath="fullbleedstripe.image"
        definition={imageDef}
        className="relative block w-full"
      >
        {image.image ? (
          <VibesitesImage
            url={image.image}
            alt={image.alt || 'Community lifestyle'}
            className="w-full h-[42vh] min-h-[330px] md:h-[56vh] md:min-h-[470px] object-cover"
            sizes="100vw"
          />
        ) : (
          <div className="w-full h-[42vh] min-h-[330px] md:h-[56vh] md:min-h-[470px] bg-[var(--vibe-color-surface)]" />
        )}
      </EditableSlotGroup>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FullBleedStripe',
  displayName: 'Full Bleed Stripe',
  tokens: {},
  slotGroups: {
    'fullbleedstripe.image': imageDef,
  },
};

(FullBleedStripe as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FullBleedStripe;
